import React, { useState, useEffect } from "react";
import { Container, Card, Form, Button, Alert, Spinner } from "react-bootstrap";
import { collection, addDoc, Timestamp } from "firebase/firestore";
import { db } from "../firebase";
import * as Yup from "yup";
import { Formik } from "formik";
import DiabetesCareAdmin from "./DiabetesCareAdmin";

const DiabetesCare = () => {
  const [registrationComplete, setRegistrationComplete] = useState(false);
  const [error, setError] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);

  // Add custom styles to the document head
  useEffect(() => {
    const styleSheet = document.createElement("style");
    styleSheet.type = "text/css";
    styleSheet.innerText = `
      .diabetes-hero {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        padding: 60px 0;
        margin-bottom: 2rem;
        border-radius: 0 0 50px 50px;
      }

      .diabetes-card {
        border: none !important;
        box-shadow: 0 0.5rem 1rem rgba(0, 0, 0, 0.15) !important;
        border-radius: 20px !important;
        transition: transform 0.3s ease !important;
      }

      .diabetes-card:hover {
        transform: translateY(-5px) !important;
      }

      .btn-diabetes {
        background: linear-gradient(135deg, #28a745 0%, #20c997 100%) !important;
        border: none !important;
        border-radius: 25px !important;
        padding: 12px 30px !important;
        font-weight: 600 !important;
        transition: all 0.3s ease !important;
        font-size: 16px !important;
      }

      .btn-diabetes:hover {
        transform: translateY(-2px) !important;
        box-shadow: 0 5px 15px rgba(40, 167, 69, 0.4) !important;
      }

      .form-control-diabetes {
        border-radius: 15px !important;
        border: 2px solid #e9ecef !important;
        padding: 15px 20px !important;
        font-size: 16px !important;
        transition: all 0.3s ease !important;
      }

      .form-control-diabetes:focus {
        border-color: #28a745 !important;
        box-shadow: 0 0 0 0.2rem rgba(40, 167, 69, 0.25) !important;
        transform: scale(1.02) !important;
      }

      .service-item {
        background: white;
        padding: 20px;
        border-radius: 15px;
        margin-bottom: 15px;
        box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
        transition: all 0.3s ease;
        border-left: 4px solid #28a745;
      }

      .service-item:hover {
        transform: translateY(-3px);
        box-shadow: 0 5px 20px rgba(0, 0, 0, 0.15);
      }

      .success-animation {
        animation: bounceIn 0.8s ease-out;
      }

      @keyframes bounceIn {
        0% {
          opacity: 0;
          transform: scale(0.3);
        }
        50% {
          opacity: 1;
          transform: scale(1.05);
        }
        70% {
          transform: scale(0.9);
        }
        100% {
          opacity: 1;
          transform: scale(1);
        }
      }

      .floating-icons {
        animation: float 3s ease-in-out infinite;
      }

      @keyframes float {
        0%, 100% {
          transform: translateY(0px);
        }
        50% {
          transform: translateY(-10px);
        }
      }

      .gradient-text {
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
      }
    `;
    document.head.appendChild(styleSheet);

    return () => {
      document.head.removeChild(styleSheet);
    };
  }, []);

  // Helper function to convert Arabic numerals to English
  const convertArabicToEnglishNumbers = (str) => {
    if (!str) return str;
    const arabicNumerals = ["٠", "١", "٢", "٣", "٤", "٥", "٦", "٧", "٨", "٩"];
    return str.replace(/[٠-٩]/g, (match) => arabicNumerals.indexOf(match));
  };

  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string().required("الاسم مطلوب"),
    phone: Yup.string()
      .required("رقم الهاتف مطلوب")
      .test(
        "is-valid-phone",
        "رقم الهاتف يجب أن يكون 11 رقم",
        function (value) {
          const convertedValue = convertArabicToEnglishNumbers(value);
          return /^[0-9]{11}$/.test(convertedValue);
        },
      ),
  });

  const initialValues = {
    name: "",
    phone: "",
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    try {
      setError(null);

      // Convert Arabic numbers to English before saving
      const registrationData = {
        name: values.name.trim(),
        phone: convertArabicToEnglishNumbers(values.phone.trim()),
        registeredAt: Timestamp.now(),
        status: "pending",
        medicalCenter: "مركز الفيروز الطبي",
        service: "رعاية مرضى السكري المجانية",
      };

      // Save to Firestore collection 'siwaDM'
      await addDoc(collection(db, "siwaDM"), registrationData);

      setRegistrationComplete(true);
      resetForm();
    } catch (err) {
      console.error("Error saving registration:", err);
      setError("فشل في حفظ التسجيل. يرجى المحاولة مرة أخرى.");
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setRegistrationComplete(false);
    setError(null);
  };

  // Show admin panel if requested
  if (showAdmin) {
    return <DiabetesCareAdmin onBack={() => setShowAdmin(false)} />;
  }

  if (registrationComplete) {
    return (
      <div
        style={{
          direction: "rtl",
          minHeight: "100vh",
          background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
        }}
      >
        <Container className="pt-5">
          <div className="success-animation">
            <Card className="diabetes-card shadow-lg border-0 text-center">
              <Card.Body className="p-5">
                <div className="mb-4">
                  <div className="floating-icons">
                    <i
                      className="bi bi-check-circle-fill text-success"
                      style={{ fontSize: "5rem" }}
                    ></i>
                  </div>
                </div>
                <h2 className="gradient-text mb-3">🎉 تم التسجيل بنجاح</h2>
                <h5 className="mb-4 text-success">شكراً لك على الثقة</h5>
                <p
                  className="mb-4 lead"
                  style={{ fontSize: "18px", lineHeight: "1.8" }}
                >
                  تم حفظ بياناتك بنجاح في نظامنا. سيتم التواصل معك قريباً من قبل
                  فريق
                  <strong> مركز الفيروز الطبي</strong> لتنسيق موعد الفحص
                  المجاني.
                </p>
                <div className="mb-4 p-3 bg-light rounded-3">
                  <p className="mb-2">
                    <i className="bi bi-telephone-fill text-primary me-2"></i>
                    <strong>للاستفسارات الفورية: 01035536523</strong>
                  </p>
                  <p className="mb-0 text-muted">
                    <i className="bi bi-clock-fill me-2"></i>
                    سيتم الاتصال بك خلال 24 ساعة
                  </p>
                </div>
                <Button
                  className="btn-diabetes"
                  size="lg"
                  onClick={resetForm}
                  style={{ minWidth: "200px" }}
                >
                  <i className="bi bi-plus-circle me-2"></i>
                  تسجيل مريض آخر
                </Button>
              </Card.Body>
            </Card>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div
      style={{
        direction: "rtl",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      }}
    >
      {/* Hero Section */}
      <div className="diabetes-hero text-center">
        <Container>
          <div className="floating-icons">
            <h1 className="display-3 mb-3" style={{ fontWeight: "700" }}>
              🩺 رعاية مرضى السكري المجانية
            </h1>
          </div>
          <h3 className="mb-4" style={{ fontWeight: "400", opacity: "0.9" }}>
            تحت إشراف مركز الفيروز الطبي
          </h3>
          <p
            className="lead mb-0"
            style={{ fontSize: "20px", maxWidth: "600px", margin: "0 auto" }}
          >
            نقدم لك فحص طبي مجاني شامل لمرضى السكري مع المتابعة الطبية المتخصصة
          </p>
        </Container>
      </div>

      <Container className="py-5">
        {/* Registration Form */}
        <div className="row justify-content-center mb-5">
          <div className="col-md-8 col-lg-6">
            <Card className="diabetes-card shadow-lg border-0">
              <Card.Header
                className="text-center"
                style={{
                  background:
                    "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
                  color: "white",
                  borderRadius: "20px 20px 0 0",
                  padding: "20px",
                }}
              >
                <h4 className="mb-0" style={{ fontWeight: "600" }}>
                  <i className="bi bi-clipboard-heart me-2"></i>
                  نموذج التسجيل
                </h4>
              </Card.Header>
              <Card.Body className="p-5">
                {error && (
                  <Alert variant="danger" className="text-center rounded-3">
                    <i className="bi bi-exclamation-triangle me-2"></i>
                    {error}
                  </Alert>
                )}

                <Formik
                  initialValues={initialValues}
                  validationSchema={validationSchema}
                  onSubmit={handleSubmit}
                >
                  {({
                    values,
                    errors,
                    touched,
                    handleChange,
                    handleBlur,
                    handleSubmit,
                    isSubmitting,
                  }) => (
                    <Form onSubmit={handleSubmit}>
                      <Form.Group className="mb-4">
                        <Form.Label
                          className="fw-bold"
                          style={{ fontSize: "16px" }}
                        >
                          <i className="bi bi-person me-2 text-primary"></i>
                          الاسم الكامل <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={values.name}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.name && errors.name}
                          placeholder="أدخل اسمك الكامل"
                          className="form-control-diabetes"
                          style={{ textAlign: "right" }}
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.name}
                        </Form.Control.Feedback>
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label
                          className="fw-bold"
                          style={{ fontSize: "16px" }}
                        >
                          <i className="bi bi-telephone me-2 text-primary"></i>
                          رقم الهاتف <span className="text-danger">*</span>
                        </Form.Label>
                        <Form.Control
                          type="tel"
                          name="phone"
                          value={values.phone}
                          onChange={handleChange}
                          onBlur={handleBlur}
                          isInvalid={touched.phone && errors.phone}
                          placeholder="أدخل رقم هاتفك (11 رقم)"
                          className="form-control-diabetes"
                          style={{ textAlign: "right" }}
                          dir="ltr"
                        />
                        <Form.Control.Feedback type="invalid">
                          {errors.phone}
                        </Form.Control.Feedback>
                        <Form.Text
                          className="text-muted"
                          style={{ fontSize: "14px" }}
                        >
                          <i className="bi bi-info-circle me-1"></i>
                          مثال: 01234567890
                        </Form.Text>
                      </Form.Group>

                      <div className="d-grid">
                        <Button
                          type="submit"
                          disabled={isSubmitting}
                          className="btn-diabetes"
                          style={{ padding: "15px 30px", fontSize: "18px" }}
                        >
                          {isSubmitting ? (
                            <>
                              <Spinner
                                as="span"
                                animation="border"
                                size="sm"
                                role="status"
                                aria-hidden="true"
                                className="me-2"
                              />
                              جاري التسجيل...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-send me-2"></i>
                              تسجيل للحصول على الفحص المجاني
                            </>
                          )}
                        </Button>
                      </div>
                    </Form>
                  )}
                </Formik>
              </Card.Body>
            </Card>
          </div>
        </div>

        {/* Service Information */}
        <div className="row">
          <div className="col-12">
            <Card
              className="diabetes-card border-0"
              style={{ background: "rgba(255, 255, 255, 0.95)" }}
            >
              <Card.Body className="p-5">
                <h4
                  className="text-center gradient-text mb-5"
                  style={{ fontSize: "28px", fontWeight: "700" }}
                >
                  <i className="bi bi-heart-pulse me-3"></i>
                  ما نقدمه لك في مركز الفيروز الطبي
                </h4>
                <div className="row">
                  <div className="col-md-6 mb-4">
                    <div className="service-item">
                      <div className="d-flex align-items-center">
                        <div className="floating-icons me-3">
                          <i
                            className="bi bi-check-circle-fill text-success"
                            style={{ fontSize: "2rem" }}
                          ></i>
                        </div>
                        <span style={{ fontSize: "16px", fontWeight: "500" }}>
                          فحص شامل مجاني لمرضى السكري
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-4">
                    <div className="service-item">
                      <div className="d-flex align-items-center">
                        <div className="floating-icons me-3">
                          <i
                            className="bi bi-check-circle-fill text-success"
                            style={{ fontSize: "2rem" }}
                          ></i>
                        </div>
                        <span style={{ fontSize: "16px", fontWeight: "500" }}>
                          تحاليل طبية متخصصة
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-4">
                    <div className="service-item">
                      <div className="d-flex align-items-center">
                        <div className="floating-icons me-3">
                          <i
                            className="bi bi-check-circle-fill text-success"
                            style={{ fontSize: "2rem" }}
                          ></i>
                        </div>
                        <span style={{ fontSize: "16px", fontWeight: "500" }}>
                          استشارة طبية مع أطباء متخصصين
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-4">
                    <div className="service-item">
                      <div className="d-flex align-items-center">
                        <div className="floating-icons me-3">
                          <i
                            className="bi bi-check-circle-fill text-success"
                            style={{ fontSize: "2rem" }}
                          ></i>
                        </div>
                        <span style={{ fontSize: "16px", fontWeight: "500" }}>
                          متابعة دورية للحالة الصحية
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-4">
                    <div className="service-item">
                      <div className="d-flex align-items-center">
                        <div className="floating-icons me-3">
                          <i
                            className="bi bi-check-circle-fill text-success"
                            style={{ fontSize: "2rem" }}
                          ></i>
                        </div>
                        <span style={{ fontSize: "16px", fontWeight: "500" }}>
                          نصائح غذائية ونمط حياة صحي
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-md-6 mb-4">
                    <div className="service-item">
                      <div className="d-flex align-items-center">
                        <div className="floating-icons me-3">
                          <i
                            className="bi bi-check-circle-fill text-success"
                            style={{ fontSize: "2rem" }}
                          ></i>
                        </div>
                        <span style={{ fontSize: "16px", fontWeight: "500" }}>
                          خدمة على أعلى مستوى من الجودة
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>

        {/* Contact Information */}
        <div className="text-center mt-5">
          <Card
            className="diabetes-card"
            style={{
              background: "linear-gradient(135deg, #28a745 0%, #20c997 100%)",
              color: "white",
            }}
          >
            <Card.Body className="p-4">
              <h5
                className="mb-4"
                style={{ fontSize: "24px", fontWeight: "600" }}
              >
                <i className="bi bi-hospital me-2"></i>
                مركز الفيروز الطبي
              </h5>
              <div className="row justify-content-center">
                <div className="col-md-6">
                  <p className="mb-3" style={{ fontSize: "18px" }}>
                    <i className="bi bi-telephone-fill me-2"></i>
                    <strong>للاستفسارات: 01035536523</strong>
                  </p>
                </div>
                <div className="col-md-6">
                  <p
                    className="mb-0"
                    style={{ fontSize: "16px", opacity: "0.9" }}
                  >
                    <i className="bi bi-clock-fill me-2"></i>
                    سيتم التواصل معك خلال 24 ساعة لتحديد موعد الفحص
                  </p>
                </div>
              </div>
            </Card.Body>
          </Card>
        </div>

        {/* Admin Access Button */}
        <div className="text-center mt-4">
          <Button
            variant="outline-dark"
            size="sm"
            onClick={() => setShowAdmin(true)}
            style={{
              borderRadius: "20px",
              padding: "8px 20px",
              fontSize: "14px",
              opacity: "0.7",
              transition: "all 0.3s ease",
            }}
            onMouseEnter={(e) => {
              e.target.style.opacity = "1";
              e.target.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.target.style.opacity = "0.7";
              e.target.style.transform = "scale(1)";
            }}
          >
            <i className="bi bi-gear me-2"></i>
            دخول المدير
          </Button>
        </div>
      </Container>
    </div>
  );
};

export default DiabetesCare;
